import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { LogoProps } from '../../components/navigation/header/Logo';
import { NavigationLink, PrimaryHeaderThemeProps } from '../../components/navigation/header/primaryHeader/PrimaryHeader';
import { CtaButton, CtaDataProps } from '../../components/navigation/header/primaryHeader/CtaButton';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { primaryHeaderHeights } from '../../components/navigation/header/header.utils';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { AMPLogo } from './AMPLogo';
import { AMPPrimaryLinks } from './AMPPrimaryLinks';
import { AMPEditions } from './AMPEditions';
import { AMPMenuIcon } from './AMPMenuIcon';
import { createStyleForBoxShadow } from '../../../theming/boxShadow';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

interface AMPPrimaryHeaderDataProps {
  links: NavigationLink[] | null;
  logo: LogoProps;
  editions: NavigationLink[] | null;
  cta: CtaDataProps | null;
  shouldShowClosedMenuIcon: boolean;
  pagePath: string;
  editionEndpoint: string;
}

const componentConfigThemeKey = 'primaryHeader';

const getPrimaryHeaderStyle = (backgroundColor: string, baseUnit: BaseUnit) => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
    [MEDIA_BREAKPOINTS.large]: {
      height: `${primaryHeaderHeights.large}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${primaryHeaderHeights.medium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${primaryHeaderHeights.small}px`,
    },
  },
  itemsBasicContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    height: '100%',
    width: 'fit-content',
    alignItems: 'center',
    gridColumnGap: `${baseUnit}px`,
  },
  itemsRightContainer: {
    alignItems: 'baseline',
    [MEDIA_BREAKPOINTS.large]: {
      paddingRight: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingRight: `${baseUnit / 2}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingRight: `${baseUnit / 2}px`,
    },
  },
  itemsLeftContainer: {
    paddingLeft: `${baseUnit}px`,
  },
  ctaWrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      height: `${primaryHeaderHeights.large}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${primaryHeaderHeights.medium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${primaryHeaderHeights.small}px`,
    },
    display: 'flex',
    alignItems: 'center',
  },
});

export const AMPPrimaryHeader: React.FunctionComponent<AMPPrimaryHeaderDataProps> = props => {
  const { boxShadowTypeNormal, backgroundColor, baseUnit } = useTheme<PrimaryHeaderThemeProps>(componentConfigThemeKey);
  const { links, logo, cta, shouldShowClosedMenuIcon, editions, pagePath, editionEndpoint } = props;
  const style = getPrimaryHeaderStyle(backgroundColor, baseUnit);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  return (
    <ErrorBoundary>
      <div className={css(style.wrapper, boxShadowStyle.style)}>
        <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
          {shouldShowClosedMenuIcon ? <AMPMenuIcon /> : null}
          <AMPLogo {...logo} />
          {links ? <AMPPrimaryLinks links={links} pagePath={pagePath} /> : null}
        </div>
        <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
          {cta ? (
            <div className={css(style.ctaWrapper)}>
              <CtaButton {...cta} />
            </div>
          ) : null}
          {editions ? <AMPEditions editions={editions} editionEndpoint={editionEndpoint} /> : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};
